import { RefObject, useCallback, useEffect, useRef, useState } from 'react'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

import {
  generateInsertValueActivityData,
  ActivityDataModel,
  EquationPartsModel,
  checkAnswer,
} from './InsertValue.utils'

interface UseInsertValueReturnType {
  variableMap: Map<string, string>
  equationParts: EquationPartsModel[]
  formValid: boolean
  answer: Map<string, string>
  handleCheckAnswer: () => void
  onInputChange: (key: string, value: string) => void
  goToNextQuestion: () => void
  inputRef: RefObject<HTMLInputElement>
}

interface UseInsertValueProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
  autofocus: boolean
  answerState: boolean | null
}

function UseInsertValue({
  onAnswerStateChange,
  onNextQuestionClick,
  difficultyLevel,
  autofocus,
  answerState,
}: UseInsertValueProps): UseInsertValueReturnType {
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const [answer, setAnswer] = useState<Map<string, string>>(new Map())
  const [activityData, setActivityData] = useState<ActivityDataModel>({
    equationString: '',
    variableMap: new Map(),
    equationParts: [],
    maxHistoryLength: 10,
  })

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  useEffect(() => {
    setAnswer(new Map())
    setActivityData(generateInsertValueActivityData(difficultyLevel, taskHistory))
  }, [taskHistory, difficultyLevel])

  const handleCheckAnswer = useCallback((): void => {
    onAnswerStateChange(checkAnswer(activityData.equationParts, activityData.variableMap, answer))
  }, [activityData.equationParts, activityData.variableMap, answer, onAnswerStateChange])

  const onInputChange = useCallback(
    (key: string, value: string) => {
      if (key === 'clear') {
        setAnswer(new Map())
        onAnswerStateChange(null)
      } else {
        const temp = new Map(answer)
        temp.set(key, value)
        setAnswer(temp)
      }
    },
    [answer, onAnswerStateChange],
  )

  const goToNextQuestion = useCallback(() => {
    setTaskHistory((prevState) => [activityData.equationString, ...prevState].slice(0, activityData.maxHistoryLength))
    onNextQuestionClick()
  }, [onNextQuestionClick, activityData.equationString, activityData.maxHistoryLength])

  return {
    variableMap: activityData.variableMap,
    equationParts: activityData.equationParts,
    formValid: answer.size > 0,
    answer,
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
    inputRef,
  }
}

export default UseInsertValue
